
import { mapActions, mapGetters } from 'vuex'
import VueSlider from 'vue-slider-component/dist-css/vue-slider-component.umd.min.js'
import { registerMap } from 'echarts/core'
import { geoMercator } from 'd3-geo'
import yearbook from '../../api/yearbook'
import 'vue-slider-component/dist-css/vue-slider-component.css'
import 'vue-slider-component/theme/default.css'
import EnerLoader from '../ui/enerLoader'
import EnerRank from './enerRank'
import EnerEchartsMap from '@/components/enerEcharts/enerEchartsMap'
import worldMap from '@/assets/geojson/worldUltra.json'

registerMap('world', worldMap)

export default {
  name: 'MapTool',
  components: { EnerLoader, EnerRank, EnerEchartsMap, VueSlider },
  data () {
    return {
      apiData: [],
      yearData: [],
      sliderYear: null,
      isTrend: false,
      isPlaying: false,
      exportMap: false,
      isHighestRank: true
    }
  },
  async fetch () {
    const serieFullCodes = [this.pageContent.map.item + '_' + this.pageContent.map.displayUnit]
    const requestParams = {
      fullCode: serieFullCodes,
      pagination: false
    }
    await yearbook.values(requestParams, this).then((data) => {
      this.apiData = data
      this.sliderYear = this.pageContent.map.year
      this.proceedYearData()
    })
  },
  computed: {
    ...mapGetters({
      mapCountry: 'page/mapCountry',
      countries: 'countries',
      pageContent: 'page/pageContent',
      mapContent: 'page/mapContent',
      mapNbDecimal: 'page/nbDecimal',
      rankingOrder: 'page/rankingOrder',
      marketing: 'marketingContent'
    }),
    mapTitle () {
      return this.pageContent.shortTitle + ' - ' + this.yearLabel
    },
    mapFileName () {
      return this.pageContent.seo.image.replace('.png', '') + '_' + this.yearLabel
    },
    mapUnitName () {
      if (this.isTrend) {
        if (this.mapContent.displayUnit === '%') {
          return 'percent point'
        }
        return '%'
      } else {
        return this.$options.filters.unitName(this.mapContent.displayUnit)
      }
    },
    levelTrendTitle () {
      if (this.isTrend) {
        return this.$i18n.t('button.title.level')
      }
      return this.$i18n.t('button.title.trend')
    },
    year () {
      if (this.isTrend) {
        return parseInt(this.mapContent.cagrYear)
      }
      return this.sliderYear
    },
    yearLabel () {
      if (this.isTrend) {
        return this.mapContent.cagrYear
      }
      return this.sliderYear
    },
    rankOrder () {
      if (this.isHighestRank === true) {
        return this.rankingOrder
      }
      return this.rankingOrder === 'top' ? 'bottom' : 'top'
    },
    nbDecimal () {
      if (this.isTrend) {
        return 1
      }
      return this.mapNbDecimal
    },
    pieces () {
      let bornes
      const pieces = []
      if (this.isTrend) {
        bornes = this.mapContent.bornes[this.mapContent.cagrYear]
      } else {
        bornes = this.mapContent.bornes[this.mapContent.year]
      }
      const n = bornes.length
      for (let i = 0; i <= n; i++) {
        const piece = {}
        if (i > 0) {
          piece.min = bornes[i - 1]
        }
        if (i < n) {
          piece.max = bornes[i]
        }
        pieces.push(piece)
      }
      pieces.forEach((piece, index) => {
        if (index === 0) {
          piece.label = this.$i18n.t('page.map.legend.below') + ' ' + piece.max
        } else if (index === (pieces.length - 1)) {
          piece.label = this.$i18n.t('page.map.legend.above') + ' ' + piece.min
        } else {
          piece.label = piece.min + ' ' + this.$i18n.t('page.map.legend.to') + ' ' + piece.max
        }
      })
      return pieces
    },
    option () {
      const projection = geoMercator()
      const scope = this
      return {
        tooltip: {
          trigger: 'item',
          showDelay: 0,
          transitionDuration: 0.2,
          formatter (params) {
            if (params.data === undefined) {
              return null
            }
            return params.data.fullName + ' ' + params.data.year + ': <b>' + params.value.toFixed(scope.nbDecimal) + '</b> ' + params.data.unit
          }
        },
        visualMap: {
          bottom: 0,
          left: 'center',
          orient: 'horizontal',
          type: 'piecewise',
          calculable: true,
          pieces: this.pieces,
          dimension: 'value',
          inRange: {
            color: [
              '#C6DADE',
              '#75AFBC',
              '#2e7f8d',
              '#015365',
              '#163a41'
            ]
          }
        },
        dataset: {
          source: this.yearData
        },
        series: {
          map: 'world',
          type: 'map',
          name: 'country',
          width: '100%',
          nameProperty: 'id',
          itemStyle: {
            borderColor: '#fff',
            color: '#fafafa',
            label: {
              show: false
            }
          },
          emphasis: {
            disabled: true
          },
          select: {
            label: {
              show: false
            },
            itemStyle: {
              color: '#000'
            }
          },
          projection: {
            project: point => projection(point),
            unproject: point => projection.invert(point)
          }
        }
      }
    }
  },
  watch: {
    sliderYear () {
      this.proceedYearData()
    },
    isTrend () {
      this.proceedYearData()
    }
  },
  methods: {
    ...mapActions({
      setMapCountry: 'page/setMapCountry'
    }),
    handleToggleTrend () {
      this.isTrend = !this.isTrend
      if (this.isTrend) {
        this.stopPlaying()
      }
    },
    handleYearDragEnd () {
      this.sliderYear = this.$refs.yearSlider.getValue()
    },
    goToNextYear () {
      if (this.sliderYear === this.$config.maxDataYear) {
        if (this.isPlaying) {
          this.stopPlaying()
        } else {
          this.sliderYear = this.$config.minDataYear
        }
      } else {
        this.sliderYear++
      }
    },
    goToPreviousYear () {
      if (this.sliderYear === this.$config.minDataYear) {
        this.sliderYear = this.$config.maxDataYear
      } else {
        this.sliderYear--
      }
    },
    stopPlaying () {
      this.isPlaying = false
      if (this.timer !== null) {
        clearInterval(this.timer)
      }
    },
    handleTogglePlayPause () {
      this.isPlaying = !this.isPlaying
      if (this.isPlaying === true) {
        if (this.sliderYear === this.$config.maxDataYear) {
          this.sliderYear = this.$config.minDataYear
        }
        this.timer = setInterval(this.goToNextYear, 1000)
      } else if (this.timer !== null) {
        clearInterval(this.timer)
      }
    },
    handleExportMap () {
      if (this.exportMap === false) {
        this.exportMap = true
      }
    },
    handleExportingMap () {
      if (this.exportMap === true) {
        this.exportMap = false
      }
    },
    handleCountrySelect (selected) {
      if (selected.length === 0) {
        this.setMapCountry(null)
      } else {
        const selectedDataIndex = selected[0].dataIndex
        if (selectedDataIndex.length === 1) {
          const selectedData = this.option.dataset.source[selectedDataIndex[0]]
          if (selectedData !== undefined) {
            const selectedCountry = this.countries.find(country => country.isoCode2 === selectedData.id)
            this.setMapCountry(selectedCountry)
          } else {
            this.setMapCountry(null)
            this.$bvModal.show('map-modal')
          }
        } else {
          this.setMapCountry(null)
        }
      }
    },
    toggleRank () {
      this.isHighestRank = !this.isHighestRank
    },
    proceedYearData () {
      const scope = this
      const cagrFirstYear = parseInt(scope.mapContent.cagrYear.split('-')[0])
      const cagrLastYear = parseInt(scope.mapContent.cagrYear.split('-')[1])

      function trendReducer (result, element) {
        if (element.zoneType === 'pays' && (element.year === cagrFirstYear || element.year === cagrLastYear)) {
          const previousElement = result.find(resultElement => resultElement.id === element.zoneCodeIso2)
          if (previousElement) {
            let value
            if (previousElement.year > element.year) {
              if (element.unitCode === '%') {
                value = previousElement.value - element.value
              } else {
                value = ((previousElement.value / element.value) - 1) * 100
              }
            } else if (element.unitCode === '%') {
              value = element.value - previousElement.value
            } else {
              value = ((element.value / previousElement.value) - 1) * 100
            }
            previousElement.value = value
            previousElement.year = scope.mapContent.cagrYear
          } else {
            const unit = element.unitCode === '%' ? 'percent point' : '%'
            const item = {
              name: element.zoneCodeIso2,
              id: element.zoneCodeIso2,
              fullName: element.zoneName,
              value: element.value,
              unit,
              year: element.year
            }
            result.push(item)
          }
        }
        return result
      }

      function reducer (result, element) {
        if (element.year === scope.year && element.zoneType === 'pays') {
          const value = element.value
          const item = {
            name: element.zoneCodeIso2,
            id: element.zoneCodeIso2,
            fullName: element.zoneName,
            value,
            unit: element.unitCode,
            year: element.year
          }
          result.push(item)
        }
        return result
      }

      if (this.isTrend) {
        this.yearData = this.apiData.reduce(trendReducer, []).filter(item => item.year === scope.mapContent.cagrYear)
      } else {
        this.yearData = this.apiData.reduce(reducer, [])
      }
    }
  },
  fetchOnServer: false
}
